// src/pages/admin/AdminResultados.js
//
// Cuadro de resultados HolaVeci: ingresos, egresos, utilidad, comisión
// inversionistas. Con gestión de egresos operativos y generación de
// cierre de período (que a su vez genera liquidaciones a inversionistas).

import React, { useEffect, useState } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Stack, TextField,
  Button, MenuItem, Alert, LinearProgress, Grid, Table, TableHead,
  TableBody, TableRow, TableCell, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AssessmentIcon    from '@mui/icons-material/Assessment';
import AddIcon           from '@mui/icons-material/Add';
import DeleteIcon        from '@mui/icons-material/Delete';
import CalculateIcon     from '@mui/icons-material/Calculate';
import LockIcon          from '@mui/icons-material/Lock';
import PeopleAltIcon     from '@mui/icons-material/PeopleAlt';
import { resultados, inversionistas as apiInv } from '../../api/tesoreriaApi';
import { useApp } from '../../context/AppContext';

const fmtCOP = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);

const inicioMesActual = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().substring(0, 10);
};
const hoyStr = () => new Date().toISOString().substring(0, 10);

export default function AdminResultados() {
  const { notificar } = useApp();
  const [periodo, setPeriodo] = useState({ desde: inicioMesActual(), hasta: hoyStr() });
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [egresos, setEgresos] = useState([]);
  const [inv, setInv] = useState([]);
  const [dialogEgreso, setDialogEgreso] = useState(false);
  const [dialogInv, setDialogInv] = useState(false);
  const [cierres, setCierres] = useState([]);

  const calcular = async () => {
    setCargando(true);
    try {
      const r = await resultados.calcular(periodo.desde, periodo.hasta);
      setDatos(r);
    } catch (e) {
      notificar(e.mensajeUsuario || 'Error calculando.', 'error');
    } finally { setCargando(false); }
  };

  const cargarAux = async () => {
    try {
      setEgresos(await resultados.listarEgresos(periodo.desde, periodo.hasta) || []);
      setInv(await apiInv.listar() || []);
      setCierres(await resultados.listarCierres() || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { calcular(); cargarAux(); }, [periodo.desde, periodo.hasta]); // eslint-disable-line

  const generarCierre = async () => {
    const notas = window.prompt('Notas del cierre (opcional):') || '';
    try {
      const r = await resultados.generarCierre(periodo.desde, periodo.hasta, notas);
      notificar(r.mensaje, 'success');
      cargarAux();
    } catch (e) {
      notificar(e.mensajeUsuario || 'Error.', 'error');
    }
  };

  const eliminarEgreso = async (id) => {
    if (!window.confirm('¿Eliminar este egreso?')) return;
    try { await resultados.eliminarEgreso(id); notificar('Eliminado.', 'success'); cargarAux(); calcular(); }
    catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <AssessmentIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontFamily="Fraunces">Cuadro de resultados</Typography>
          <Typography variant="body2" color="text.secondary">
            Estado financiero HolaVeci por período.
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="date" size="small" label="Desde"
                InputLabelProps={{ shrink: true }} value={periodo.desde}
                onChange={e => setPeriodo({ ...periodo, desde: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="date" size="small" label="Hasta"
                InputLabelProps={{ shrink: true }} value={periodo.hasta}
                onChange={e => setPeriodo({ ...periodo, hasta: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" startIcon={<CalculateIcon />} onClick={calcular}>
                  Recalcular
                </Button>
                <Button variant="contained" startIcon={<LockIcon />} onClick={generarCierre}>
                  Generar cierre
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {cargando && <LinearProgress />}

      {datos && (
        <>
          {/* Cuadro grande */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography variant="caption" color="text.secondary">INGRESOS NETOS</Typography>
                <Typography variant="h4" color="success.main" fontWeight={700}>
                  {fmtCOP(datos.ingresos.netos)}
                </Typography>
                <Typography variant="caption" display="block">
                  Brutos {fmtCOP(datos.ingresos.brutos)} · IVA {fmtCOP(datos.ingresos.iva)}
                </Typography>
                <Typography variant="caption">{datos.ingresos.facturasPagadas} facturas pagadas</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography variant="caption" color="text.secondary">EGRESOS OPERATIVOS</Typography>
                <Typography variant="h4" color="error.main" fontWeight={700}>
                  {fmtCOP(datos.egresos.operativos)}
                </Typography>
                <Typography variant="caption">Guías: {fmtCOP(datos.egresos.guias)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography variant="caption" color="text.secondary">UTILIDAD BRUTA</Typography>
                <Typography variant="h4" color={datos.utilidadBruta >= 0 ? 'success.main' : 'error.main'} fontWeight={700}>
                  {fmtCOP(datos.utilidadBruta)}
                </Typography>
                <Typography variant="caption">Antes de inversionistas</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: datos.utilidadNeta >= 0 ? 'success.light' : 'error.light' }}>
                <CardContent>
                <Typography variant="caption">UTILIDAD NETA</Typography>
                <Typography variant="h4" fontWeight={700}>
                  {fmtCOP(datos.utilidadNeta)}
                </Typography>
                <Typography variant="caption">Descontando inv. {fmtCOP(datos.comInversionistas)}</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          {/* Desglose inversionistas */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontFamily="Fraunces">
                  <PeopleAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Distribución a inversionistas
                </Typography>
                <Button startIcon={<AddIcon />} onClick={() => setDialogInv(true)}>
                  Nuevo inversionista
                </Button>
              </Stack>
              {datos.inversionistas.length === 0 ? (
                <Alert severity="info">Sin inversionistas registrados aún.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Inversionista</TableCell>
                      <TableCell align="right">% Utilidad</TableCell>
                      <TableCell align="right">Monto período</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {datos.inversionistas.map(i => (
                      <TableRow key={i.inversionistaId}>
                        <TableCell>{i.nombreCompleto}</TableCell>
                        <TableCell align="right">{i.porcentajeUtilidad}%</TableCell>
                        <TableCell align="right"><b>{fmtCOP(i.monto)}</b></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Egresos operativos del período */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontFamily="Fraunces">Egresos operativos del período</Typography>
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialogEgreso(true)}>
              Nuevo egreso
            </Button>
          </Stack>
          {egresos.length === 0 ? (
            <Alert severity="info">Sin egresos operativos registrados en el período.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell><TableCell>Concepto</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell align="right">Monto</TableCell><TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {egresos.map(e => (
                  <TableRow key={e.egresoId}>
                    <TableCell>{e.fecha?.substring(0,10)}</TableCell>
                    <TableCell>{e.concepto}</TableCell>
                    <TableCell>{e.categoria}</TableCell>
                    <TableCell align="right">{fmtCOP(e.monto)}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => eliminarEgreso(e.egresoId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Cierres previos */}
      {cierres.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontFamily="Fraunces" gutterBottom>Cierres previos</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Período</TableCell>
                  <TableCell align="right">Ingresos</TableCell>
                  <TableCell align="right">Egresos</TableCell>
                  <TableCell align="right">Util. bruta</TableCell>
                  <TableCell align="right">Util. neta</TableCell>
                  <TableCell align="right">A inversionistas</TableCell>
                  <TableCell>Cerrado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cierres.map(c => (
                  <TableRow key={c.cierreId}>
                    <TableCell>{c.periodoDesde?.substring(0,10)} → {c.periodoHasta?.substring(0,10)}</TableCell>
                    <TableCell align="right">{fmtCOP(c.ingresosNetos)}</TableCell>
                    <TableCell align="right">{fmtCOP(c.egresosOperativos + c.egresosGuias)}</TableCell>
                    <TableCell align="right">{fmtCOP(c.utilidadBruta)}</TableCell>
                    <TableCell align="right"><b>{fmtCOP(c.utilidadNeta)}</b></TableCell>
                    <TableCell align="right">{fmtCOP(c.comisionInversionistas)}</TableCell>
                    <TableCell>{c.fechaCierre?.substring(0,10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <DialogEgreso abierto={dialogEgreso} onCerrar={() => setDialogEgreso(false)}
                    onCreado={() => { setDialogEgreso(false); cargarAux(); calcular(); }} />
      <DialogInversionista abierto={dialogInv} onCerrar={() => setDialogInv(false)}
                    onCreado={() => { setDialogInv(false); cargarAux(); calcular(); }} />
    </Container>
  );
}

function DialogEgreso({ abierto, onCerrar, onCreado }) {
  const { notificar } = useApp();
  const [f, setF] = useState({
    fecha: hoyStr(), concepto: '', categoria: 'HOSTING', monto: '', notas: '',
  });
  const [g, setG] = useState(false);
  useEffect(() => { if (abierto) setF({ fecha: hoyStr(), concepto: '', categoria: 'HOSTING', monto: '', notas: '' }); }, [abierto]);

  const submit = async () => {
    if (!f.concepto || !f.monto) { notificar('Concepto y monto son requeridos.', 'error'); return; }
    setG(true);
    try {
      await resultados.crearEgreso({ ...f, monto: Number(f.monto) });
      notificar('Egreso registrado.', 'success');
      onCreado();
    } catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
    finally { setG(false); }
  };
  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Nuevo egreso operativo</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField fullWidth type="date" label="Fecha" InputLabelProps={{ shrink: true }}
                     value={f.fecha} onChange={e => setF({ ...f, fecha: e.target.value })} />
          <TextField fullWidth label="Concepto" value={f.concepto}
                     onChange={e => setF({ ...f, concepto: e.target.value })} />
          <TextField select fullWidth label="Categoría" value={f.categoria}
                     onChange={e => setF({ ...f, categoria: e.target.value })}>
            <MenuItem value="HOSTING">Hosting</MenuItem>
            <MenuItem value="HERRAMIENTAS">Herramientas</MenuItem>
            <MenuItem value="LEGAL">Legal</MenuItem>
            <MenuItem value="MARKETING">Marketing</MenuItem>
            <MenuItem value="SUELDOS">Sueldos</MenuItem>
            <MenuItem value="OTRO">Otro</MenuItem>
          </TextField>
          <TextField fullWidth type="number" label="Monto (COP)" value={f.monto}
                     onChange={e => setF({ ...f, monto: e.target.value })} />
          <TextField fullWidth multiline rows={2} label="Notas" value={f.notas}
                     onChange={e => setF({ ...f, notas: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={g}>Registrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function DialogInversionista({ abierto, onCerrar, onCreado }) {
  const { notificar } = useApp();
  const [f, setF] = useState({
    nombreCompleto: '', numeroDocumento: '', tipoDocumento: 'CC',
    telefono: '', correo: '', montoInvertido: '', porcentajeUtilidad: 7,
    banco: '', tipoCuenta: 'AHORROS', cuentaBancaria: '',
  });
  const [g, setG] = useState(false);
  const submit = async () => {
    if (!f.nombreCompleto || !f.numeroDocumento) {
      notificar('Nombre y documento requeridos.', 'error'); return;
    }
    setG(true);
    try {
      await apiInv.crear({
        ...f,
        montoInvertido: Number(f.montoInvertido) || 0,
        porcentajeUtilidad: Number(f.porcentajeUtilidad),
      });
      notificar('Inversionista creado.', 'success');
      onCreado();
    } catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
    finally { setG(false); }
  };
  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Nuevo inversionista</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField fullWidth label="Nombre completo" value={f.nombreCompleto}
                     onChange={e => setF({ ...f, nombreCompleto: e.target.value })} />
          <Stack direction="row" spacing={1}>
            <TextField select label="Tipo" value={f.tipoDocumento} sx={{ minWidth: 100 }}
                       onChange={e => setF({ ...f, tipoDocumento: e.target.value })}>
              <MenuItem value="CC">CC</MenuItem><MenuItem value="CE">CE</MenuItem>
              <MenuItem value="NIT">NIT</MenuItem>
            </TextField>
            <TextField fullWidth label="Documento" value={f.numeroDocumento}
                       onChange={e => setF({ ...f, numeroDocumento: e.target.value })} />
          </Stack>
          <TextField fullWidth label="Teléfono" value={f.telefono}
                     onChange={e => setF({ ...f, telefono: e.target.value })} />
          <TextField fullWidth label="Correo" type="email" value={f.correo}
                     onChange={e => setF({ ...f, correo: e.target.value })} />
          <TextField fullWidth type="number" label="Monto invertido (COP)" value={f.montoInvertido}
                     onChange={e => setF({ ...f, montoInvertido: e.target.value })} />
          <TextField fullWidth type="number" label="% utilidad (default 7)" value={f.porcentajeUtilidad}
                     onChange={e => setF({ ...f, porcentajeUtilidad: e.target.value })}
                     inputProps={{ step: 0.5 }} />
          <Stack direction="row" spacing={1}>
            <TextField fullWidth label="Banco" value={f.banco}
                       onChange={e => setF({ ...f, banco: e.target.value })} />
            <TextField select label="Tipo cuenta" value={f.tipoCuenta} sx={{ minWidth: 130 }}
                       onChange={e => setF({ ...f, tipoCuenta: e.target.value })}>
              <MenuItem value="AHORROS">Ahorros</MenuItem>
              <MenuItem value="CORRIENTE">Corriente</MenuItem>
              <MenuItem value="NEQUI">Nequi</MenuItem>
              <MenuItem value="DAVIPLATA">Daviplata</MenuItem>
            </TextField>
          </Stack>
          <TextField fullWidth label="Número de cuenta" value={f.cuentaBancaria}
                     onChange={e => setF({ ...f, cuentaBancaria: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={g}>Registrar</Button>
      </DialogActions>
    </Dialog>
  );
}
